import React from "react";
import { Box, Collapse, Select, MenuItem, IconButton, InputBase, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { selectSx, menuProps, noteMultilineSx } from "bundles/Internal/components/common/FloatingTimer";

export default function IdlePanel({
  open,
  selectedProjectId,
  setSelectedProjectId,
  selectedTaskId,
  setSelectedTaskId,
  selectedProject,
  coloredProjects,
  note,
  setNote,
  onCreateProject,
  onCreateTask,
}) {
  return (
    <Collapse in={open} unmountOnExit>
      <Box sx={{ px: 2.5, pb: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {/* Project */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Select
            value={selectedProjectId}
            onChange={(e) => {
              setSelectedProjectId(e.target.value);
              setSelectedTaskId("");
            }}
            displayEmpty
            size="small"
            variant="outlined"
            MenuProps={menuProps}
            sx={{ ...selectSx, flex: 1, minWidth: 0 }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>Select project</Typography>
                );
              }
              const p = coloredProjects.find((cp) => cp.id === value);
              if (!p) return null;
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                    {p.name}
                  </Typography>
                </Box>
              );
            }}
          >
            {coloredProjects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: p.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                    {p.name}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <IconButton
            aria-label="Create project"
            onClick={onCreateProject}
            disableRipple
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "12px",
              border: "1px solid hsl(140 8% 88%)",
              backgroundColor: "hsl(140 10% 95%)",
              color: "hsl(150 38% 17%)",
              transition: "background-color 0.15s ease, border-color 0.15s ease",
              "&:hover": { backgroundColor: "#ffffff" },
            }}
          >
            <Add sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Task */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Select
            value={selectedTaskId}
            onChange={(e) => setSelectedTaskId(e.target.value)}
            displayEmpty
            size="small"
            variant="outlined"
            MenuProps={menuProps}
            sx={{ ...selectSx, flex: 1, minWidth: 0 }}
            renderValue={(value) => {
              if (!value) {
                return (
                  <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>Select task</Typography>
                );
              }
              const t = (selectedProject?.tasks || []).find((tt) => tt.id === value);
              if (!t) return null;
              return (
                <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                  {t.name.replace(/_/g, " ")}
                </Typography>
              );
            }}
          >
            {(selectedProject?.tasks || []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>{t.name.replace(/_/g, " ")}</Typography>
              </MenuItem>
            ))}
          </Select>
          <IconButton
            aria-label="Create task"
            onClick={onCreateTask}
            disableRipple
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "12px",
              border: "1px solid hsl(140 8% 88%)",
              backgroundColor: "hsl(140 10% 95%)",
              color: "hsl(150 38% 17%)",
              transition: "background-color 0.15s ease, border-color 0.15s ease",
              "&:hover": { backgroundColor: "#ffffff" },
            }}
          >
            <Add sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Note */}
        <InputBase
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (optional)"
          multiline
          minRows={3}
          maxRows={3}
          sx={noteMultilineSx}
        />
      </Box>
    </Collapse>
  );
}
